import type { ReactNode } from "react";
import { Link } from "@/lib/router-compat";
import { ArrowRight } from "lucide-react";
import SiteNav, { BookingButton, WriteMeLink } from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import PageSeo from "@/components/site/PageSeo";

export const BX_STRATEGY_SLUG = "strategie-marketing-pme-bordeaux";
export const BX_STRATEGY_PATH = `/blog/${BX_STRATEGY_SLUG}`;
export const BX_STRATEGY_TITLE = "Stratégie marketing PME à Bordeaux : par où commencer en 2026 | CMO Partner";
export const BX_STRATEGY_DESCRIPTION =
  "Positionnement, leviers d'acquisition, CRM, KPIs et alignement commercial : les 5 fondations d'une stratégie marketing efficace pour les PME de Bordeaux et de Gironde.";
export const BX_STRATEGY_H1 = "Stratégie marketing pour PME à Bordeaux : par où commencer en 2026 ?";
const URL = `https://www.cmopartner.fr${BX_STRATEGY_PATH}`;

export const BX_STRATEGY_FAQ = [
  { q: "Combien faut-il budgéter pour le marketing en PME ?", a: "Un repère courant : 5 à 10 % du chiffre d'affaires pour une PME en croissance, 3 à 5 % pour une PME stable. En dessous de 3 %, il est difficile de rester visible face à la concurrence ; au-delà de 10 %, vérifiez que vos KPIs justifient l'investissement." },
  { q: "Le marché bordelais a-t-il des spécificités ?", a: "Oui. Le tissu économique bordelais fonctionne beaucoup en réseau. La proximité et la confiance restent des critères de choix importants, notamment en B2B, et un prestataire local capable d'être présent physiquement garde un avantage au moment de la décision." },
  { q: "Quels outils marketing utilisent les PME à Bordeaux ?", a: "HubSpot est devenu une référence CRM et marketing pour les PME B2B. Brevo est très utilisé pour l'email par les PME B2C. Pour la publicité, Google Ads reste dominant, avec Meta en complément pour le B2C." },
  { q: "Comment choisir un bon prestataire marketing à Bordeaux ?", a: "Privilégiez les prestataires qui cherchent à comprendre vos objectifs business avant de proposer des actions. Méfiez-vous des offres standard vendues sans audit préalable. Demandez des exemples de résultats mesurables (leads, chiffre d'affaires, ROI), pas seulement des exemples de réalisations." },
  { q: "Quelle différence entre un directeur marketing externalisé et un consultant ?", a: "Le consultant produit un plan et des recommandations. Le directeur marketing externalisé (Fractional CMO) prend la responsabilité de l'exécution : il définit la stratégie ET pilote sa mise en œuvre, y compris le management des prestataires et de l'équipe interne." },
];

const PERSON = { "@type": "Person", name: "Tiphany Rigaudias", jobTitle: ["Fractional CMO", "Directrice marketing externalisée", "Directrice digitale externalisée"], knowsAbout: ["Marketing B2B", "CRM", "Marketing automation", "IA appliquée au marketing", "Formation des équipes marketing"], url: "https://www.cmopartner.fr/a-propos", sameAs: ["https://linkedin.com/in/tiphanyrigaudias"] };

export const BX_STRATEGY_SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: BX_STRATEGY_H1,
    description: BX_STRATEGY_DESCRIPTION,
    datePublished: "2026-09-28",
    dateModified: "2026-09-28",
    inLanguage: "fr-FR",
    about: { "@type": "Place", name: "Bordeaux" },
    author: PERSON,
    publisher: { "@type": "Organization", name: "CMO Partner", url: "https://www.cmopartner.fr/", founder: PERSON },
    mainEntityOfPage: { "@type": "WebPage", "@id": URL },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: BX_STRATEGY_FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: "https://www.cmopartner.fr/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://www.cmopartner.fr/blog" },
      { "@type": "ListItem", position: 3, name: "Stratégie marketing pour PME à Bordeaux", item: URL },
    ],
  },
];

const H2 = ({ children }: { children: string }) => (
  <h2 className="text-2xl font-bold leading-tight md:text-3xl">
    {children}
    <span className="text-primary">.</span>
  </h2>
);
const H3 = ({ children }: { children: string }) => <h3 className="mt-10 text-xl font-bold">{children}</h3>;
const P = ({ children }: { children: ReactNode }) => <p className="mt-5 text-lg leading-relaxed text-muted-foreground">{children}</p>;
const A = ({ to, children }: { to: string; children: ReactNode }) => (
  <Link to={to} className="text-foreground underline underline-offset-4 hover:text-primary">{children}</Link>
);

const blockers: [string, string][] = [
  ["1. Le dirigeant est aussi le chef de projet marketing", "Dans une PME de 10 à 50 personnes, c'est souvent le fondateur ou le directeur général qui gère le marketing entre deux sujets opérationnels. Résultat : des actions ponctuelles, sans cohérence ni mesure."],
  ["2. L'équipe marketing exécute sans cap stratégique", "Quand un responsable marketing existe, il est souvent seul, débordé par le volume de tâches et sans interlocuteur pour fixer les priorités. Il produit, mais ne pilote pas."],
  ["3. Le budget est trop serré pour un CMO à temps plein", "Le profil qui résoudrait le problème, un directeur marketing expérimenté, coûte 80 à 150 k€ par an en coût total employeur. Une enveloppe hors de portée pour beaucoup de PME avant un certain niveau de chiffre d'affaires."],
];

const foundations: [string, ReactNode][] = [
  ["1. Clarifier le positionnement et la proposition de valeur", "Avant toute action, une question doit avoir une réponse limpide : pourquoi un client vous choisit-il plutôt qu'un concurrent ? Pas une réponse générique (« qualité, proximité, service »), mais un avantage concurrentiel précis, défendable et vérifiable. Sans positionnement clair, toutes les actions marketing sont du bruit."],
  ["2. Identifier 2 ou 3 leviers d'acquisition prioritaires", "Une PME ne peut pas tout faire en même temps. L'erreur classique : répartir le budget sur six canaux et n'en maîtriser aucun. La bonne approche : identifier les 2 ou 3 canaux où se trouve votre cible et y concentrer les ressources. Pour les PME B2B en Nouvelle-Aquitaine, les leviers les plus efficaces en 2026 sont généralement le SEO local, LinkedIn et la visibilité dans les moteurs de recherche IA (GEO). Pour les PME B2C, l'email, le SEO produit et l'influence locale donnent souvent les meilleurs retours."],
  ["3. Déployer un CRM et structurer le cycle de vie client", <>Un CRM bien paramétré est le système nerveux de votre marketing. Il vous dit d'où viennent vos leads, lesquels convertissent, à quelle étape ils bloquent et quelle est la valeur de vos clients dans le temps. Sans CRM, vous pilotez à l'aveugle ; avec un CRM, vous amplifiez ce qui marche et arrêtez ce qui ne marche pas. Pour aller plus loin : <A to="/blog/implementer-crm-bordeaux-hubspot-salesforce">Implémenter un CRM à Bordeaux en 2026</A>.</>],
  ["4. Suivre des indicateurs reliés à la croissance", "Le problème des PME n'est pas le manque de données, mais l'absence d'indicateurs reliés à la croissance. Compter les publications LinkedIn ou les impressions n'a aucun intérêt si l'on ne mesure pas les leads qualifiés, le coût d'acquisition et le taux de conversion du pipeline. Trois KPIs suffisent pour piloter le marketing d'une PME : le nombre de leads entrants qualifiés par semaine, le taux de conversion lead-client et le coût d'acquisition client (CAC)."],
  ["5. Aligner marketing et commercial", "Dans les PME, marketing et commercial fonctionnent souvent en silo : le marketing génère des leads que la vente ne traite pas, ou la vente se plaint de leads non qualifiés. La solution : définir ensemble le profil de client idéal (ICP), la procédure de qualification et le délai de traitement des leads (SLA)."],
];

const choices: [string, ReactNode][] = [
  ["Une agence marketing locale", "adaptée si vous avez déjà une direction marketing et cherchez de l'exécution (campagnes, contenus, SEA). Une agence ne remplace pas une direction stratégique."],
  ["Un consultant marketing", "adapté pour un audit ponctuel ou un plan d'action. Il ne gère pas l'exécution."],
  ["Un Fractional CMO", <>adapté si vous avez besoin d'une direction marketing opérationnelle sans recruter en CDI. Il prend en charge la stratégie, le pilotage de l'exécution et le management des prestataires, de 2 à 12 jours par mois selon le format. Pour une PME bordelaise en phase de structuration ou de croissance, le Fractional CMO offre généralement le meilleur équilibre entre expertise, coût et rapidité de démarrage. Le comparatif détaillé est ici : <A to="/blog/cmo-externalise-vs-agence-vs-recrutement">CMO externalisé, agence ou CDI</A>.</>],
];

const readNext = [
  { label: "Fractional CMO à Bordeaux", to: "/fractional-cmo-bordeaux" },
  { label: "Implémenter un CRM à Bordeaux en 2026", to: "/blog/implementer-crm-bordeaux-hubspot-salesforce" },
  { label: "CMO externalisé, agence ou CDI", to: "/blog/cmo-externalise-vs-agence-vs-recrutement" },
];

const BordeauxStrategyArticle = () => (
  <div className="min-h-screen bg-background text-foreground">
    <PageSeo path={BX_STRATEGY_PATH} title={BX_STRATEGY_TITLE} description={BX_STRATEGY_DESCRIPTION} />
    <SiteNav />
    <main>
      <article>
        <header className="border-b border-border px-5 pb-14 pt-20 md:pt-28">
          <div className="mx-auto max-w-3xl">
            <nav className="mb-8 text-sm text-muted-foreground" aria-label="Fil d'Ariane">
              <Link to="/" className="hover:text-primary">Accueil</Link>
              <span className="px-2">/</span>
              <Link to="/blog" className="hover:text-primary">Blog</Link>
              <span className="px-2">/</span>
              <span className="text-foreground">Stratégie marketing pour PME à Bordeaux</span>
            </nav>
            <h1 className="text-4xl font-bold leading-[1.1] md:text-5xl">{BX_STRATEGY_H1}</h1>
            <p className="mt-8 text-sm text-muted-foreground">
              Par <Link to="/a-propos" className="font-semibold text-foreground hover:text-primary">CMO Partner — Fractional CMO</Link> · Mis à jour : <time dateTime="2026-09-28">septembre 2026</time>
            </p>
            <P>La métropole bordelaise concentre un tissu économique dense : un écosystème startup actif autour de la French Tech Bordeaux, et de nombreuses PME et ETI dans le vin, l'aéronautique, le numérique et le commerce. Pourtant, beaucoup de ces entreprises n'ont pas de stratégie marketing structurée. Ce guide s'adresse aux dirigeants de PME en Gironde et en Nouvelle-Aquitaine qui veulent savoir concrètement par où commencer pour construire un marketing qui génère de la croissance.</P>
          </div>
        </header>

        <div className="px-5 py-16 md:py-20">
          <div className="mx-auto max-w-3xl space-y-16">
            <section>
              <H2>Pourquoi beaucoup de PME bordelaises n'ont pas de stratégie marketing</H2>
              <P>Trois blocages reviennent systématiquement :</P>
              {blockers.map(([t, d]) => (<div key={t}><H3>{t}</H3><P>{d}</P></div>))}
            </section>

            <section>
              <H2>Les 5 fondations d'une stratégie marketing efficace en PME</H2>
              {foundations.map(([t, d], i) => (
                <div key={t}>
                  <H3>{t}</H3>
                  <P>{d}</P>
                  {i === 2 && (
                    <div className="mt-10 rounded-lg bg-secondary p-7">
                      <p className="text-lg font-semibold">Identifions vos leviers prioritaires en 30 minutes.</p>
                      <div className="mt-5"><BookingButton label="Réserver un appel" location="blog_bx_strategy_mid" /></div>
                    </div>
                  )}
                </div>
              ))}
            </section>

            <section>
              <H2>Bordeaux, Mérignac, Pessac, Bègles : quel accompagnement selon votre localisation ?</H2>
              <P>La métropole bordelaise concentre la majorité des ressources marketing de la région : agences, freelances, intégrateurs CRM, consultants. Les entreprises installées hors de Bordeaux (Mérignac, Pessac, Bègles, Arcachon, Libourne) y ont le même accès. Les formats hybrides, avec quelques journées de présence physique par mois et le reste à distance, se sont imposés et correspondent aux besoins réels des PME régionales.</P>
            </section>

            <section>
              <H2>Quel type d'accompagnement marketing choisir à Bordeaux ?</H2>
              <ul className="mt-5 space-y-4">
                {choices.map(([t, d]) => (
                  <li key={t} className="flex gap-3 text-lg leading-relaxed text-muted-foreground">
                    <span className="mt-2.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                    <span><strong className="text-foreground">{t}</strong> : {d}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </div>
      </article>

      <section className="border-t border-border bg-secondary px-5 py-20">
        <div className="mx-auto max-w-3xl">
          <H2>FAQ : stratégie marketing PME à Bordeaux</H2>
          <div className="mt-10">
            {BX_STRATEGY_FAQ.map((f) => (
              <details key={f.q} className="group border-t border-border py-6">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-lg font-bold">
                  {f.q}
                  <span className="text-primary transition-transform group-open:rotate-45" aria-hidden="true">+</span>
                </summary>
                <p className="mt-3 leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border px-5 py-16">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-xl font-bold">À lire aussi</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {readNext.map((r) => (
              <Link key={r.to} to={r.to} className="rounded-lg border border-border p-5 transition-colors hover:border-primary">
                <span className="font-semibold">{r.label}</span>
                <ArrowRight className="mt-4 h-4 w-4 text-primary" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section id="booking" className="scroll-mt-16 bg-foreground px-5 py-24 text-center text-background md:py-28">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-4xl font-bold md:text-5xl">Par où commencer dans votre PME<span className="text-primary"> ?</span></h2>
          <p className="mt-6 text-lg text-background/60">30 minutes pour identifier vos leviers marketing prioritaires.</p>
          <div className="mt-10"><BookingButton label="Réserver un appel de 30 minutes" location="blog_bx_strategy_final" large /><WriteMeLink /></div>
          <p className="mt-6 text-sm text-background/50">Basée à Bordeaux · Disponible partout en France</p>
        </div>
      </section>
    </main>
    <SiteFooter />
  </div>
);

export default BordeauxStrategyArticle;
